import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import {
  COLUNAS, STATUS_ACORDO, formatarValorColuna, isFecharAcordoDesabilitado,
  isQuebrarAcordoDesabilitado, getTituloBotaoCancelar, isEnviarEmailDesabilitado, isImprimirDesabilitado,
  isAnaliseDesabilitado, isDistribuicaoPersonalizadaDesabilitado,
} from './acordosController';
import TableAcordoAccordion from './TableAcordoAccordion';
import DialogHistoricoAcordo from './DialogHistoricoAcordo';
import DialogFecharAcordo from './DialogFecharAcordo';
import ConfirmDialog from './ConfirmDialog';
import DialogQuebrarAcordo from './DialogQuebrarAcordo';
import DialogEnviarEmailAcordo from './DialogEnviarEmailAcordo';
import DialogAnaliseSimulacao from './DialogAnaliseSimulacao';
import DialogDistribuicaoPersonalizada from './DialogDistribuicaoPersonalizada';
import { cancelarSimulacao, cancelarProposta, imprimirAcordoService } from '../../../../services/core/processos/processosAcordosService';
import { openPagePDF } from '../../../../helpers/utils/utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';
import * as C from '../../../theme/colors.constants';

/**
 * Grid "Acordos" da aba Recebimentos (AW2-1133 Etapa 3) — colunas fixas
 * (`COLUNAS`, `acordosController.js`), header e corpo table-driven sobre o
 * MESMO array (mesmo padrão de `TableTitulosProcesso.jsx`, nunca uma lista
 * de `TableCell` hardcoded em paralelo). `TableSortLabel` só nas colunas com
 * `ordenarPor` não-nulo.
 *
 * As 7 ações têm `id` estável + ícone/hint fixos (`ACOES_PLACEHOLDER`); cada
 * uma fica habilitada só quando `AcoesCelula` recebe um handler para o seu
 * `id` via prop `handlers` (map `id -> fn(acordo)`) — as demais continuam
 * `disabled` automaticamente. "Exibir Detalhes" (AW2-1133 Etapa 5) é a 1ª a
 * ganhar handler real; as outras 6 ganham nas etapas 6-11 do plano, cada
 * uma só adicionando 1 entrada ao map, sem restruturar `AcoesCelula`.
 *
 * Além do handler, uma ação pode declarar `desabilitado(acordo, podeModificarFase)`
 * (opcional, default nenhum = nunca bloqueia) para um gate de negócio
 * per-linha — "Fechar Acordo" (Etapa 6) é a 1ª a precisar disso
 * (`isFecharAcordoDesabilitado`, `acordosController.js`, confirmado por
 * leitura direta de `Acordos.jsx` real); "Exibir Detalhes" não tem gate,
 * então continua habilitado sempre que há handler.
 *
 * Chevron de expandir (AW2-1133 Etapa 4) é real — padrão MUI v5
 * "collapsible table row": uma `TableRow` extra por acordo, com uma única
 * `TableCell colSpan` contendo `Collapse` + `TableAcordoAccordion`. Estado
 * de quais linhas estão expandidas (`expandidos`, `Set` de ids) é local a
 * este componente (UI pura) e resetado sempre que `acordos` mudar de
 * referência (troca de processo/página/ordenação/toggle) — mesmo raciocínio
 * de "reseedar contra o dado de origem" já documentado no módulo.
 */
const MENSAGEM_ERRO_IMPRESSAO = 'Não foi possível imprimir o Termo de Acordo. Tente novamente.';

// `imprimirAcordoService` usa `responseType: 'blob'` — uma resposta de ERRO
// também chega como Blob (não JSON), por isso o corpo precisa passar por
// `.text()` antes de `JSON.parse`, mesmo tratamento do
// `onConfirmImprimirTermoAcordo` real. `try/catch` extra aqui (ausente na
// tela real) é defensivo — cobre uma resposta sem `.response` (falha de
// rede) ou um corpo que não é JSON, casos em que a tela real deixaria a
// promise rejeitar sem tratamento. `?? MENSAGEM_ERRO_IMPRESSAO` (achado em
// code review) cobre o caso de um 400 com corpo JSON válido mas sem campo
// `message` — sem o fallback, `setErroImprimir(undefined)` faz
// `Boolean(erroImprimir)` ser `false` e o Snackbar simplesmente não abre,
// exatamente o silêncio que este `try/catch` extra deveria evitar. Função de
// módulo (não interna ao componente) — não depende de nenhuma prop/state,
// só do `exception` recebido.
async function extrairMensagemErroImpressao(exception) {
  try {
    const texto = await exception.response.data.text();
    return JSON.parse(texto).message ?? MENSAGEM_ERRO_IMPRESSAO;
  } catch {
    return MENSAGEM_ERRO_IMPRESSAO;
  }
}

const ACOES_PLACEHOLDER = [
  { id: 'detalhes', Icone: InfoOutlinedIcon, hint: 'Exibir Detalhes' },
  {
    id: 'fechar', Icone: HandshakeOutlinedIcon, hint: 'Fechar Acordo', desabilitado: isFecharAcordoDesabilitado,
  },
  {
    // `hint` como função (AW2-1133 Etapa 7) — rótulo varia por
    // `idStatusAcordo` (Cancelar Simulação/Cancelar Proposta/Quebrar Acordo),
    // confirmado por leitura direta de `getTituloBotaoCancelar` real.
    id: 'quebrar', Icone: LinkOffIcon, hint: acordo => getTituloBotaoCancelar(acordo?.idStatusAcordo), desabilitado: isQuebrarAcordoDesabilitado,
  },
  {
    id: 'email', Icone: EmailOutlinedIcon, hint: 'Enviar Termo por E-mail', desabilitado: isEnviarEmailDesabilitado,
  },
  {
    id: 'imprimir', Icone: PrintOutlinedIcon, hint: 'Imprimir Termo', desabilitado: isImprimirDesabilitado,
  },
  {
    id: 'analise', Icone: AccessTimeOutlinedIcon, hint: 'Análise da Proposta', desabilitado: isAnaliseDesabilitado,
  },
  {
    id: 'distribuicao', Icone: PollOutlinedIcon, hint: 'Distribuição Personalizada', desabilitado: isDistribuicaoPersonalizadaDesabilitado,
  },
];

function StatusAcordo({ idStatusAcordo }) {
  const status = STATUS_ACORDO[idStatusAcordo];
  return (
    <Tooltip title={status?.label ?? 'Status desconhecido'} arrow>
      <Box
        sx={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: status?.cor ?? C.warmGrey,
        }}
      />
    </Tooltip>
  );
}
StatusAcordo.propTypes = { idStatusAcordo: PropTypes.number };
StatusAcordo.defaultProps = { idStatusAcordo: null };

function AcoesCelula({
  acordo, expandido, onToggleExpandir, handlers, podeModificarFase, extra,
}) {
  return (
    <Box sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}>
      {ACOES_PLACEHOLDER.map(({
        id, Icone, hint, desabilitado,
      }) => {
        const handler = handlers[id];
        // 3º argumento (`extra`, AW2-1133 Etapa 10) — só "análise" (Relógio)
        // precisa de contexto fora de `acordo`/`podeModificarFase`
        // (`idUsuarioLogado`/`ehFaseAcordoSeguro`); as demais funções de
        // gate simplesmente ignoram o argumento extra que não declaram.
        const bloqueadoPorRegra = desabilitado ? desabilitado(acordo, podeModificarFase, extra) : false;
        const inativo = !handler || bloqueadoPorRegra;
        const tituloHint = typeof hint === 'function' ? hint(acordo) : hint;
        return (
          <Tooltip key={id} title={tituloHint} arrow>
            <span>
              <IconButton
                size="small"
                disabled={inativo}
                aria-label={tituloHint}
                onClick={inativo ? undefined : () => handler(acordo)}
                sx={{ color: C.navy, p: 0.4 }}
              >
                <Icone fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
      <Tooltip title={expandido ? 'Ocultar Parcelas' : 'Exibir Parcelas'} arrow>
        <span>
          <IconButton
            size="small"
            onClick={onToggleExpandir}
            aria-label={expandido ? 'Ocultar parcelas do acordo' : 'Exibir parcelas do acordo'}
            sx={{ color: C.navy, p: 0.4 }}
          >
            {expandido ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
AcoesCelula.propTypes = {
  acordo: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }).isRequired,
  expandido: PropTypes.bool.isRequired,
  onToggleExpandir: PropTypes.func.isRequired,
  handlers: PropTypes.objectOf(PropTypes.func),
  podeModificarFase: PropTypes.bool,
  extra: PropTypes.object,
};
AcoesCelula.defaultProps = {
  handlers: {},
  podeModificarFase: false,
  extra: null,
};

export default function TableAcordos({
  acordos, ordenacao, onChangeOrdenacao, idProcesso, idFaseProcesso, podeModificarFase, ehFaseAcordoSeguro,
  onAcordoMutado,
}) {
  // Insumo do gate "Relógio" (AW2-1133 Etapa 10) — lido direto via
  // `useSelector` neste componente-folha, mesmo padrão já usado por outros
  // dialogs do módulo que precisam de um dado pontual do Redux
  // (`DialogEnviarOutraFase.jsx`, `NovoAcordoPassoTres.jsx` etc.), em vez de
  // subir para o container só para repassar de volta.
  const idUsuarioLogado = useSelector(states => states.authentication.userInformation.idUsuario);
  const extraAcoes = useMemo(
    () => ({ idUsuarioLogado, ehFaseAcordoSeguro }),
    [idUsuarioLogado, ehFaseAcordoSeguro],
  );

  const [expandidos, setExpandidos] = useState(new Set());
  const [dialogHistorico, setDialogHistorico] = useState({ aberto: false, texto: '' });
  const [dialogFechar, setDialogFechar] = useState({ aberto: false, acordo: null });
  // "Mãozinha cortada" (AW2-1133 Etapa 7) — 2 dos 3 caminhos (Simulação/
  // Proposta) reaproveitam o mesmo `ConfirmDialog` genérico; `tipo` decide
  // qual service chamar em `onConfirmarCancelar`. O 3º caminho (default,
  // Quebrar Acordo) usa `DialogQuebrarAcordo.jsx` (dialog próprio, com
  // select de Motivo).
  const [dialogCancelar, setDialogCancelar] = useState({ aberto: false, acordo: null, tipo: null });
  const [dialogQuebrar, setDialogQuebrar] = useState({ aberto: false, acordo: null });
  const [dialogEmail, setDialogEmail] = useState({ aberto: false, acordo: null });
  // "Imprimir" (AW2-1133 Etapa 9) — confirmação de 3 vias só aparece quando a
  // 1ª parcela está "Aguardando Envio" (`idStatusParcelaAcordo === 1`,
  // confirmado por leitura direta de `Acordos.jsx` real); nos demais casos a
  // impressão dispara direto, sem dialog. Nenhum dos 2 caminhos mantém um
  // dialog aberto durante o `await` (o de confirmação fecha antes, igual
  // `DialogFecharAcordo.jsx`) — mas `TableAcordos` em si sobrevive a uma
  // troca de processo (nenhum `key={processo?.id}` no container), então
  // `enviandoImprimirRef`/`erroImprimir` continuam expostos ao mesmo risco
  // já coberto por `useSessaoDialogMutacao` em `dialogCancelar`/
  // `DialogFecharAcordo.jsx`: sem ele, trocar de processo com uma impressão
  // em voo travaria um clique novo até a chamada antiga resolver, e um erro
  // do processo ANTERIOR podia aparecer sobre a tela do processo ATUAL
  // (achado em code review). Reaproveitado mesmo sem dialog persistente —
  // `dialogImprimir.aberto` ainda serve de "open" para o efeito de
  // reabertura.
  const [dialogImprimir, setDialogImprimir] = useState({ aberto: false, acordo: null });
  const [erroImprimir, setErroImprimir] = useState('');
  const [dialogAnalise, setDialogAnalise] = useState({ aberto: false, acordo: null });
  const [dialogDistribuicao, setDialogDistribuicao] = useState({ aberto: false, acordo: null });
  const [enviandoCancelar, setEnviandoCancelar] = useState(false);
  const [resultadoCancelar, setResultadoCancelar] = useState(null);

  const { sessaoRef: sessaoCancelarRef, enviandoRef: enviandoCancelarRef } = useSessaoDialogMutacao(
    dialogCancelar.aberto,
    idProcesso,
    () => {
      setEnviandoCancelar(false);
      setResultadoCancelar(null);
    },
  );

  const { sessaoRef: sessaoImprimirRef, enviandoRef: enviandoImprimirRef } = useSessaoDialogMutacao(
    dialogImprimir.aberto,
    idProcesso,
    () => setErroImprimir(''),
  );

  useEffect(() => {
    setExpandidos(new Set());
  }, [acordos]);

  const onFecharCancelar = () => setDialogCancelar({ aberto: false, acordo: null, tipo: null });
  const onFecharResultadoCancelar = () => setResultadoCancelar(null);

  // Port de `handleOnClickCancelarSimulacao`/`handleOnClickCancelarProposta`
  // reais — igual a `DialogQuebrarAcordo.jsx`, e diferente de
  // `DialogFecharAcordo.jsx` (Etapa 6): fecha o dialog só no `finally`,
  // DEPOIS do `await`, incondicionalmente (sucesso OU erro).
  const onConfirmarCancelar = async () => {
    if (enviandoCancelarRef.current || !dialogCancelar.acordo) {
      return;
    }

    const sessaoAlvo = sessaoCancelarRef.current;
    const { acordo, tipo } = dialogCancelar;
    const nomeAcao = tipo === 'simulacao' ? 'Simulação' : 'Proposta';
    enviandoCancelarRef.current = true;
    setEnviandoCancelar(true);

    try {
      if (tipo === 'simulacao') {
        await cancelarSimulacao(acordo.id, idProcesso);
      } else {
        await cancelarProposta(acordo.id, idProcesso);
      }
      if (sessaoCancelarRef.current === sessaoAlvo) {
        onFecharCancelar();
        setResultadoCancelar({ severidade: 'success', mensagem: `${nomeAcao} cancelada com sucesso.` });
      }
    } catch {
      if (sessaoCancelarRef.current === sessaoAlvo) {
        onFecharCancelar();
        setResultadoCancelar({
          severidade: 'error',
          mensagem: `Não foi possível cancelar a ${nomeAcao}. Tente novamente.`,
        });
      }
    } finally {
      if (sessaoCancelarRef.current === sessaoAlvo) {
        enviandoCancelarRef.current = false;
        setEnviandoCancelar(false);
      }
      if (onAcordoMutado) {
        onAcordoMutado();
      }
    }
  };

  const onFecharErroImprimir = () => setErroImprimir('');
  const onFecharDialogImprimir = () => setDialogImprimir({ aberto: false, acordo: null });

  // Port de `onConfirmImprimirTermoAcordo` real — `openPagePDF` (reaproveitado
  // de `helpers/utils/utils.js`, nunca reimplementado) abre o PDF numa nova
  // aba; `URL.revokeObjectURL` NÃO é chamado porque a própria tela real
  // (`openPagePDF`) também não chama — paridade deliberada, não um
  // esquecimento (ver Etapa 9 do plano de arquitetura). `useCallback` mantém
  // a identidade estável entre renders — necessário porque `onImprimir`
  // (abaixo) e o `useMemo` de `handlers` dependem dela.
  const onImprimirAcordo = useCallback(async (idAcordo, alteraStatusParcela) => {
    if (enviandoImprimirRef.current) {
      return;
    }
    const sessaoAlvo = sessaoImprimirRef.current;
    enviandoImprimirRef.current = true;

    try {
      const { data } = await imprimirAcordoService(idAcordo, alteraStatusParcela);
      openPagePDF(data);
    } catch (exception) {
      const mensagem = await extrairMensagemErroImpressao(exception);
      if (sessaoImprimirRef.current === sessaoAlvo) {
        setErroImprimir(mensagem);
      }
    } finally {
      if (sessaoImprimirRef.current === sessaoAlvo) {
        enviandoImprimirRef.current = false;
      }
      // Incondicional (sucesso OU erro) — mesmo `finally` do
      // `onConfirmImprimirTermoAcordo` real (`updatePageAcordo()`), já que
      // `alteraStatusParcela=true` muda o status da 1ª parcela no backend e
      // a grid/accordion precisam refletir isso.
      if (onAcordoMutado) {
        onAcordoMutado();
      }
    }
  }, [enviandoImprimirRef, sessaoImprimirRef, onAcordoMutado]);

  // Porta `handleClickImprimir`/o `onClick` inline do ícone real: só abre a
  // confirmação de 3 vias quando a 1ª parcela está "Aguardando Envio"
  // (`idStatusParcelaAcordo === 1`); qualquer outro status imprime direto
  // com `alteraStatusParcela=false`. `useCallback` (dependente só de
  // `onImprimirAcordo`, já estável) é o que torna o `useMemo` de `handlers`
  // abaixo genuinamente memoizado — sem isso, `onImprimir` mudava de
  // identidade a cada render e invalidava o `useMemo` sempre.
  const onImprimir = useCallback((acordo) => {
    if (acordo?.parcelas?.[0]?.idStatusParcelaAcordo === 1) {
      setDialogImprimir({ aberto: true, acordo });
      return;
    }
    onImprimirAcordo(acordo.id, false);
  }, [onImprimirAcordo]);

  const onToggleExpandir = (idAcordo) => {
    setExpandidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(idAcordo)) {
        novo.delete(idAcordo);
      } else {
        novo.add(idAcordo);
      }
      return novo;
    });
  };

  // `useMemo` — a maioria das entradas só usa setters estáveis (`useState`),
  // então em princípio nunca precisaria recriar o objeto (achado em code
  // review original, Etapa 5). "imprimir" (Etapa 9) quebra essa garantia ao
  // chamar `onImprimirAcordo` direto, que lê `onAcordoMutado` — por isso as
  // deps incluem `[onAcordoMutado]` agora, não `[]`.
  const handlers = useMemo(() => ({
    detalhes: acordo => setDialogHistorico({ aberto: true, texto: acordo.texto }),
    fechar: acordo => setDialogFechar({ aberto: true, acordo }),
    // Roteamento por `idStatusAcordo` (AW2-1133 Etapa 7) — confirmado por
    // leitura direta de `onClickButtonQuebrarAcordo` real: Simulação(3) e
    // Proposta(4) reaproveitam o `ConfirmDialog` genérico; qualquer outro
    // status (só chega aqui como Ativo=0, os demais já vêm desabilitados por
    // `isQuebrarAcordoDesabilitado`) abre `DialogQuebrarAcordo`.
    quebrar: (acordo) => {
      if (acordo.idStatusAcordo === 3) {
        setDialogCancelar({ aberto: true, acordo, tipo: 'simulacao' });
      } else if (acordo.idStatusAcordo === 4) {
        setDialogCancelar({ aberto: true, acordo, tipo: 'proposta' });
      } else {
        setDialogQuebrar({ aberto: true, acordo });
      }
    },
    email: acordo => setDialogEmail({ aberto: true, acordo }),
    // Diferente das demais entradas (que só abrem um dialog via setter
    // estável), "imprimir" pode chamar `onImprimirAcordo` DIRETO — que lê
    // `onAcordoMutado` (prop, não garantidamente estável) — por isso este
    // `useMemo` depende de `onAcordoMutado`, não `[]`; recriar as demais
    // closures (que só usam setters estáveis) nesse recálculo raro é
    // inofensivo.
    imprimir: onImprimir,
    analise: acordo => setDialogAnalise({ aberto: true, acordo }),
    distribuicao: acordo => setDialogDistribuicao({ aberto: true, acordo }),
  }), [onImprimir]);

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUNAS.map(coluna => (
              <TableCell key={coluna.campo} align={coluna.align}>
                {coluna.ordenarPor ? (
                  <TableSortLabel
                    active={ordenacao.campo === coluna.ordenarPor}
                    direction={ordenacao.direcao.toLowerCase()}
                    onClick={() => onChangeOrdenacao(coluna.ordenarPor)}
                  >
                    {coluna.label}
                  </TableSortLabel>
                ) : coluna.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {acordos.map((acordo) => {
            const expandido = expandidos.has(acordo.id);
            return (
              <React.Fragment key={acordo.id}>
                <TableRow hover>
                  {COLUNAS.map((coluna) => {
                    if (coluna.campo === 'acoes') {
                      return (
                        <TableCell key={coluna.campo} align={coluna.align}>
                          <AcoesCelula
                            acordo={acordo}
                            expandido={expandido}
                            onToggleExpandir={() => onToggleExpandir(acordo.id)}
                            handlers={handlers}
                            podeModificarFase={podeModificarFase}
                            extra={extraAcoes}
                          />
                        </TableCell>
                      );
                    }
                    if (coluna.campo === 'idStatusAcordo') {
                      return (
                        <TableCell key={coluna.campo} align={coluna.align}>
                          <StatusAcordo idStatusAcordo={acordo.idStatusAcordo} />
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={coluna.campo} align={coluna.align}>
                        {formatarValorColuna(coluna.campo, acordo)}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={COLUNAS.length} sx={{ py: 0, borderBottom: expandido ? undefined : 'none' }}>
                    <Collapse in={expandido} unmountOnExit>
                      <Box sx={{ py: 1.5 }}>
                        <TableAcordoAccordion parcelas={acordo.parcelas} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      <DialogHistoricoAcordo
        open={dialogHistorico.aberto}
        texto={dialogHistorico.texto}
        onClose={() => setDialogHistorico({ aberto: false, texto: '' })}
      />
      <DialogFecharAcordo
        open={dialogFechar.aberto}
        acordo={dialogFechar.acordo}
        idProcesso={idProcesso}
        onClose={() => setDialogFechar({ aberto: false, acordo: null })}
        onMutado={onAcordoMutado}
      />
      <ConfirmDialog
        open={dialogCancelar.aberto}
        title={dialogCancelar.tipo === 'simulacao' ? 'Cancelar Simulação' : 'Cancelar Proposta'}
        text={`Deseja realmente cancelar esta ${dialogCancelar.tipo === 'simulacao' ? 'Simulação' : 'Proposta'}?`}
        onConfirmar={onConfirmarCancelar}
        onFechar={onFecharCancelar}
        confirmando={enviandoCancelar}
      />
      <DialogQuebrarAcordo
        open={dialogQuebrar.aberto}
        acordo={dialogQuebrar.acordo}
        idProcesso={idProcesso}
        idFaseProcesso={idFaseProcesso}
        onClose={() => setDialogQuebrar({ aberto: false, acordo: null })}
        onMutado={onAcordoMutado}
      />
      <DialogEnviarEmailAcordo
        open={dialogEmail.aberto}
        acordo={dialogEmail.acordo}
        idProcesso={idProcesso}
        onClose={() => setDialogEmail({ aberto: false, acordo: null })}
        onMutado={onAcordoMutado}
      />
      <DialogAnaliseSimulacao
        open={dialogAnalise.aberto}
        acordo={dialogAnalise.acordo}
        idProcesso={idProcesso}
        onClose={() => setDialogAnalise({ aberto: false, acordo: null })}
        onMutado={onAcordoMutado}
      />
      <DialogDistribuicaoPersonalizada
        open={dialogDistribuicao.aberto}
        acordo={dialogDistribuicao.acordo}
        idProcesso={idProcesso}
        onClose={() => setDialogDistribuicao({ aberto: false, acordo: null })}
        onMutado={onAcordoMutado}
      />
      <Dialog open={dialogImprimir.aberto} onClose={onFecharDialogImprimir} maxWidth="sm" fullWidth>
        <DialogTitle>Imprimir Termo de Acordo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ao imprimir a primeira parcela você também poderá alterar seu Status para
            &quot;Aguardando Pagamento&quot;. Que ação você deseja executar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onFecharDialogImprimir}>Cancelar</Button>
          <Button
            onClick={() => {
              onFecharDialogImprimir();
              onImprimirAcordo(dialogImprimir.acordo.id, false);
            }}
          >
            Somente imprimir
          </Button>
          <Button
            onClick={() => {
              onFecharDialogImprimir();
              onImprimirAcordo(dialogImprimir.acordo.id, true);
            }}
            variant="contained"
          >
            Imprimir e atualizar o Status
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(resultadoCancelar)}
        autoHideDuration={4000}
        onClose={onFecharResultadoCancelar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={resultadoCancelar?.severidade ?? 'info'} variant="filled" onClose={onFecharResultadoCancelar}>
          {resultadoCancelar?.mensagem}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(erroImprimir)}
        autoHideDuration={4000}
        onClose={onFecharErroImprimir}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErroImprimir}>
          {erroImprimir}
        </Alert>
      </Snackbar>
    </>
  );
}

TableAcordos.propTypes = {
  acordos: PropTypes.array,
  ordenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  onChangeOrdenacao: PropTypes.func,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idFaseProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  podeModificarFase: PropTypes.bool,
  ehFaseAcordoSeguro: PropTypes.bool,
  onAcordoMutado: PropTypes.func,
};

TableAcordos.defaultProps = {
  acordos: [],
  ordenacao: { campo: 'nroAcordo', direcao: 'DESC' },
  onChangeOrdenacao: null,
  idProcesso: null,
  idFaseProcesso: null,
  ehFaseAcordoSeguro: false,
  podeModificarFase: false,
  onAcordoMutado: null,
};
